import React from "react";
import { useReveal } from "../hooks/useReveal";

export default function WhyItWorks() {
  const head = useReveal();
  const body = useReveal();
  return (
    <section
      id="why"
      data-testid="why-section"
      className="section relative overflow-hidden bg-[#020305]"
    >
      <div className="container-x relative z-10">
        <div ref={head} className="reveal max-w-3xl mb-12 lg:mb-16">
          <span className="font-mono text-sm uppercase tracking-[0.3em] text-white/55">
            / 03 — The Mechanism
          </span>
          <h2
            className="kinetic mt-6"
            style={{ fontSize: "clamp(2.6rem, 6vw, 5.4rem)", lineHeight: 0.95 }}
          >
            <span className="gradient-text">WHY CHEWING</span>
            <br />
            <span className="outline-text-blue">CHANGES STATE.</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div
            ref={body}
            className="reveal lg:col-span-7 space-y-6 text-white/75 text-lg leading-relaxed"
          >
            <p className="text-xl lg:text-2xl text-white font-light leading-snug">
              Chewing activates the{" "}
              <span style={{ color: "var(--blue-bright)" }}>trigeminal nerve</span>. One of the most
              important nerves in the human body, heavily connected to alertness, facial
              activation, sensory awareness, nervous system regulation, and cognitive
              engagement.
            </p>
            <p>
              When chewing resistance is engineered correctly, the body becomes
              physically engaged in a rhythmic, grounding pattern that anchors
              attention and reduces distraction.
            </p>
            <p
              className="text-xl lg:text-2xl text-white font-light leading-snug py-3 border-l-2 pl-6"
              style={{ borderColor: "var(--blue-bright)" }}
            >
              Protos was designed specifically around this mechanism.
            </p>
            <div className="grid sm:grid-cols-3 gap-3">
              {[
                ["Not", "candy", false],
                ["Not", "stimulant gum", false],
                ["A", "performance ritual", true],
              ].map(([prefix, label, highlight], i) => (
                <div
                  key={i}
                  data-testid={`why-pill-${i}`}
                  className={`card-dark p-5 text-center relative overflow-hidden ${
                    highlight ? "ring-1 ring-[var(--blue-bright)]" : ""
                  }`}
                  style={
                    highlight
                      ? {
                          background:
                            "linear-gradient(180deg, rgba(31,179,255,0.18), rgba(31,179,255,0.03))",
                          boxShadow:
                            "0 0 0 1px rgba(63,226,255,0.45), 0 0 32px rgba(31,179,255,0.25) inset",
                        }
                      : undefined
                  }
                >
                  {highlight && (
                    <span
                      aria-hidden
                      className="absolute -inset-8 -z-10 blur-2xl opacity-70 pointer-events-none"
                      style={{
                        background:
                          "radial-gradient(circle at 50% 50%, rgba(31,179,255,0.45), transparent 60%)",
                      }}
                    />
                  )}
                  <span
                    className={`font-display uppercase tracking-[0.15em] ${
                      highlight ? "text-white text-base" : "text-white text-2xl"
                    }`}
                  >
                    {highlight ? (
                      prefix
                    ) : (
                      <span className="inline-flex items-baseline gap-1">
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          className="self-center text-red-400/80"
                          aria-hidden
                        >
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                          <path d="M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                        <span>{prefix}</span>
                      </span>
                    )}
                  </span>
                  <div
                    className={`mt-1 font-display uppercase ${
                      highlight
                        ? "text-2xl lg:text-3xl"
                        : "text-lg text-white/70 line-through decoration-red-400/50"
                    }`}
                    style={highlight ? { color: "var(--blue-bright)" } : undefined}
                  >
                    {label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Visual side panel — animated trigeminal map abstraction */}
          <div className="lg:col-span-5">
            <div className="card-dark p-6 lg:p-8 h-full relative overflow-hidden">
              {/* Neural texture background */}
              <img
                src="/assets/neural-blue.jpg"
                alt=""
                aria-hidden
                className="absolute inset-0 w-full h-full object-cover opacity-25 mix-blend-screen"
                loading="lazy"
              />
              <div
                className="absolute inset-0 -z-0 opacity-50 blur-2xl"
                style={{
                  background:
                    "radial-gradient(circle at 70% 30%, rgba(31,179,255,0.3), transparent 60%)",
                }}
              />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(2,3,5,0.55) 0%, rgba(2,3,5,0.8) 100%)",
                }}
              />
              <div className="relative z-10">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45 mb-4">
                Trigeminal Activation
              </div>
              <svg
                viewBox="0 0 400 300"
                className="w-full h-auto"
                style={{ color: "var(--blue-bright)" }}
              >
                <defs>
                  <linearGradient id="lineGrad" x1="0" x2="1">
                    <stop offset="0%" stopColor="#1FB3FF" stopOpacity="0" />
                    <stop offset="50%" stopColor="#3FE2FF" stopOpacity="1" />
                    <stop offset="100%" stopColor="#1FB3FF" stopOpacity="0" />
                  </linearGradient>
                </defs>
                {[80, 130, 180, 230].map((y, i) => (
                  <g key={i}>
                    <line
                      x1="40"
                      y1={y}
                      x2="360"
                      y2={y}
                      stroke="rgba(255,255,255,0.06)"
                      strokeWidth="1"
                    />
                    <line
                      x1="40"
                      y1={y}
                      x2="360"
                      y2={y}
                      stroke="url(#lineGrad)"
                      strokeWidth="1.5"
                    >
                      <animate
                        attributeName="stroke-dashoffset"
                        from="0"
                        to="-200"
                        dur={`${4 + i * 0.7}s`}
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="stroke-dasharray"
                        values="0 320; 80 240; 0 320"
                        dur={`${4 + i * 0.7}s`}
                        repeatCount="indefinite"
                      />
                    </line>
                  </g>
                ))}
                {[
                  [120, 80, "Awareness"],
                  [220, 130, "Alertness"],
                  [160, 180, "Regulation"],
                  [280, 230, "Engagement"],
                ].map(([x, y, label], i) => (
                  <g key={i}>
                    <circle cx={x} cy={y} r="5" fill="#3FE2FF">
                      <animate
                        attributeName="r"
                        values="5;8;5"
                        dur="2.4s"
                        begin={`${i * 0.4}s`}
                        repeatCount="indefinite"
                      />
                    </circle>
                    <text
                      x={Number(x) + 14}
                      y={Number(y) + 4}
                      fill="rgba(255,255,255,0.85)"
                      fontSize="12"
                      fontFamily="Manrope,sans-serif"
                      style={{ letterSpacing: "0.05em" }}
                    >
                      {label}
                    </text>
                  </g>
                ))}
              </svg>
              <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                Signal · Rhythm · Anchor
              </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
